import React from 'react';

import DataTable from '@components/DataTable';
import ProjectsPaywall from '@components/ProjectsPaywall';
import BottomSection from '@components/pages/Project/Layouts/Database/BottomSection';
import useCurrentUser from '@hooks/useCurrentUser';
import useDatatableColumnConfig from '@hooks/useDatatableColumnConfig';
import { DatabaseProps, RawProjectData } from '@interfaces/projects';
import { project_path } from '@routes';

import Key, { KeyItem } from './Key';
import KeyIcon from './KeyIcon';
import TopSection from './TopSection';

export interface LayoutProps<TData, TValue> extends DatabaseProps<TData, TValue> {
  keyData: RawProjectData[];
}

const Layout = <TData, TValue>({
  data,
  keyData,
  paywallData,
  project,
  projectMedia,
  unlocked,
}: LayoutProps<TData, TValue>): JSX.Element => {
  const { currentUser } = useCurrentUser();
  const projectImagePath = project_path(project.slug);

  const keyMap = keyData.reduce((acc, item) => {
    acc[item.columnVal] = item.color;
    return acc;
  }, {});

  const { columnConfigFn } = useDatatableColumnConfig(projectImagePath, (cellContent, cell) => {
    if (cell.column.getIsFirstColumn()) {
      if (keyMap[cell.getValue()] !== undefined) {
        const keyColor = keyMap[cell.getValue()] as string;
        cellContent = (
          <div className="flex items-center justify-center">
            <KeyIcon color={keyColor} />
          </div>
        );
      }
    }

    return cellContent;
  });

  const isCurrentUserCorpOrGroupSubscriber = currentUser?.is_corp_or_group_subscriber;
  const showBottomSection = isCurrentUserCorpOrGroupSubscriber || project.notes;

  return (
    <>
      <TopSection
        accessLevel={project.accessLevel}
        authors={project.authors}
        description={project.description}
        mediaSourcePath={projectImagePath}
        projectMedia={projectMedia}
        title={project.title}
        titleCallout={project.titleCallout}
      />
      {!unlocked ? (
        <>
          <div className="relative before:absolute before:bottom-0 before:left-0 before:z-10 before:h-[300px] before:w-full before:bg-[linear-gradient(to_top,#fff_0%,rgba(255,255,255,0)_100%)] before:content-['']"></div>
          <div className="sticky bottom-0 z-50">
            <ProjectsPaywall {...paywallData} leadType={project.leadType} />
          </div>
        </>
      ) : (
        <div className="w-container-lg mx-auto mt-32 max-w-container-lg px-16">
          <span id="no-paywall"></span>
          <Key keyItems={keyData as unknown as KeyItem[]} />
          <DataTable
            columns={data.columns}
            columnConfigFn={columnConfigFn}
            data={data.data}
            hiddenCols={data.hiddenCols}
            pinnedCols={data.pinnedCols}
            projectId={project.id}
            sortCols={data.sortCols}
            showSearch
          />
          {showBottomSection && (
            <BottomSection
              id={project.id}
              isCurrentUserCorpOrGroupSubscriber={isCurrentUserCorpOrGroupSubscriber}
              notes={project.notes}
              slug={project.slug}
            />
          )}
        </div>
      )}
    </>
  );
};

export default Layout;
